import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Lock, Mail } from 'lucide-react';

import { ApiError } from '../../../shared/api/api-client.js';
import { useApi } from '../../../shared/api/api-context.js';
import { formatErrorWithSystemTime } from '../../../shared/formatters/index.js';
import { useTranslation } from '../../../shared/use-translation.js';
import { useBooking } from '../../booking/state/booking-context.js';
import { ProviderButtons } from './provider-buttons.js';

interface LoginResponse {
  readonly accessToken: string;
  readonly guestId: string;
  readonly email: string;
}

interface GoogleSignInResponse {
  readonly accessToken: string;
  readonly guestId: string;
  readonly email: string;
}

// Guest sign-in form shared by the sign-in page and the in-context
// SignInDialog — onSuccess decides what happens next (navigate or close).
export function SignInForm({ onSuccess }: { readonly onSuccess: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const api = useApi();
  const booking = useBooking();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (email.trim().length === 0) {
      errors.email = t('auth.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = t('auth.emailInvalid');
    }
    if (password.length === 0) {
      errors.password = t('auth.passwordRequired');
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResendVerification = async (): Promise<void> => {
    if (email.trim().length === 0) return;
    setResending(true);
    setResendSuccess(null);
    try {
      await api.post('/v1/auth/guest/resend-verification', {
        email: email.trim().toLowerCase(),
      });
      setError(null);
      setErrorCode(null);
      setResendSuccess(
        `Verification email sent to ${email.trim()}. Please check your inbox and spam folder.`,
      );
    } catch {
      setError(null);
      setErrorCode(null);
      setResendSuccess(
        `If an unverified account exists for ${email.trim()}, a verification email has been sent.`,
      );
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError(null);
    setErrorCode(null);
    setResendSuccess(null);
    try {
      const result = await api.post<LoginResponse>('/v1/auth/guest/login', {
        email: email.trim().toLowerCase(),
        password,
      });
      api.setAccessToken(result.accessToken);
      booking.setGuestAuth(result.accessToken, result.guestId);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorCode(err.code);
        setError(formatErrorWithSystemTime(err.message));
      } else {
        const message = err instanceof Error ? err.message : t('auth.signInError');
        setError(formatErrorWithSystemTime(message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async (credential: string): Promise<void> => {
    setLoading(true);
    setError(null);
    setErrorCode(null);
    setResendSuccess(null);
    try {
      const result = await api.post<GoogleSignInResponse>('/v1/auth/guest/google', {
        idToken: credential,
      });
      api.setAccessToken(result.accessToken);
      booking.setGuestAuth(result.accessToken, result.guestId);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorCode(err.code);
        setError(formatErrorWithSystemTime(err.message));
      } else {
        const message = err instanceof Error ? err.message : t('auth.googleError');
        setError(formatErrorWithSystemTime(message));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div>
          <label htmlFor="signin-email" className="ibe-label">
            {t('auth.email')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="signin-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              placeholder={t('auth.emailPlaceholder')}
              className="ibe-control pl-10"
              aria-invalid={fieldErrors.email !== undefined}
              disabled={loading}
            />
          </div>
          {fieldErrors.email !== undefined && <p className="ibe-error">{fieldErrors.email}</p>}
        </div>

        <div>
          <label htmlFor="signin-password" className="ibe-label">
            {t('auth.password')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="signin-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              placeholder={t('auth.passwordPlaceholder')}
              className="ibe-control pl-10 pr-10"
              aria-invalid={fieldErrors.password !== undefined}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => {
                setShowPassword((s) => !s);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {fieldErrors.password !== undefined && (
            <p className="ibe-error">{fieldErrors.password}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(e) => {
                setKeepSignedIn(e.target.checked);
              }}
              className="h-4 w-4 accent-primary"
              disabled={loading}
            />
            {t('auth.keepSignedIn')}
          </label>
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-on-primary-container hover:underline"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>

        <button type="submit" disabled={loading} className="ibe-primary-button w-full">
          {loading ? t('auth.signingIn') : t('auth.signIn')}
        </button>

        {resendSuccess !== null && (
          <div className="ibe-banner ibe-banner-info text-left" role="status">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{resendSuccess}</p>
          </div>
        )}

        {error !== null && (
          <div
            className={`rounded-lg p-3.5 text-sm ${
              error.includes('1 attempt remaining')
                ? 'border border-amber-500/30 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200'
                : 'border border-destructive/20 bg-destructive/10 text-destructive'
            }`}
            role="alert"
          >
            {error.includes('1 attempt remaining') && (
              <div className="mb-1.5 flex items-center gap-1.5 font-semibold text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <span>Account Lock Warning</span>
              </div>
            )}
            <p className="leading-snug">{error}</p>

            {(errorCode === 'GUEST_EMAIL_NOT_VERIFIED' ||
              error.includes('verify your email') ||
              error.includes('locked until') ||
              error.includes('attempts remaining')) && (
              <div className="mt-2.5 border-t border-current/15 pt-2 text-xs">
                <span>Haven't received your confirmation email? </span>
                <button
                  type="button"
                  onClick={() => void handleResendVerification()}
                  disabled={resending || email.trim().length === 0}
                  className="font-semibold underline hover:no-underline disabled:opacity-50"
                >
                  {resending ? 'Sending email...' : 'Resend verification email'}
                </button>
              </div>
            )}
          </div>
        )}
      </form>

      <div className="mt-6">
        <ProviderButtons
          onGoogleCredential={(credential) => {
            void handleGoogle(credential);
          }}
          disabled={loading}
        />
      </div>
    </>
  );
}
